import { useCallback } from 'react';
import { branchOp } from 'store/ducks/DataEntry/Branch';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import contractualArrangementsOp from 'store/ducks/DataEntry/ContractualInformation/operations';
import ApiService from 'utils/ApiCalls';
import {
  AllocationItem,
  UseLoadAllocationProps,
} from 'store/ducks/Global/types';

const LOAD_CONFIG = {
  branch: {
    fetchFn: (id: number) => branchOp.fetchBranchesByEntity({ entity_id: id }),
    titleKey: 'branch_name',
  },
  function: {
    fetchFn: (id: number) =>
      functionOp.fetchFunctionsByEntity({ entity_id: id }),
    titleKey: 'function_name',
  },
  tpsp: {
    fetchFn: (id: number) =>
      ApiService.getListOfThirdPartyServiceProvidersByEntity(id),
    titleKey: 'third_party_service_provider_name',
  },
  contractualArrangements: {
    fetchFn: (id: number) =>
      contractualArrangementsOp.fetchListOfContractualArrangementsByEntity(id),
    titleKey: 'contract_name',
  },
  ictSystems: {
    fetchFn: (id: number) => ApiService.getListOfICTSystemsByEntity(id),
    titleKey: 'name',
  },
  ictServices: {
    fetchFn: (id: number) =>
      ApiService.getListOfICTServicesByContractualArrangement(id),
    titleKey: 'description',
  },
} as const;

export const useLoadAllocation = ({
  onBranchLoad,
  onFunctionLoad,
  onTpspLoad,
  onContractualArrangementsLoad,
  onIctSystemsLoad,
  onIctServicesLoad,
}: UseLoadAllocationProps) => {
  const callbackMap = {
    branch: onBranchLoad,
    function: onFunctionLoad,
    tpsp: onTpspLoad,
    contractualArrangements: onContractualArrangementsLoad,
    ictSystems: onIctSystemsLoad,
    ictServices: onIctServicesLoad,
  };

  const loadAllocation = useCallback(
    async (
      type:
        | 'branch'
        | 'function'
        | 'tpsp'
        | 'contractualArrangements'
        | 'ictSystems'
        | 'ictServices',
      entityId: number,
    ) => {
      try {
        const config = LOAD_CONFIG[type];
        const callback = callbackMap[type as keyof typeof callbackMap];

        if (!callback) return;

        const response = await config.fetchFn(entityId);
        const data = Array.isArray(response?.data)
          ? response.data
          : response?.data
            ? [response.data]
            : [];

        const items: AllocationItem[] = data.map((item: any) => ({
          key: String(item.id),
          title: item[config.titleKey as keyof typeof item],
          ...item,
        }));

        callback(items);
      } catch (err) {
        console.error(err);
      }
    },
    [
      onBranchLoad,
      onFunctionLoad,
      onTpspLoad,
      onContractualArrangementsLoad,
      onIctSystemsLoad,
      onIctServicesLoad,
    ],
  );

  return { loadAllocation };
};
